import { EnvironmentProviders, ErrorHandler, Provider, inject, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { environment } from 'src/environments/environment';

/**
 * Browser telemetry for the shared observability stack.
 *
 * Errors and route/pageload timings go to the central GlitchTip
 * (Sentry-compatible) instance at glitchtip.ra.dev. Backend traces go
 * separately to Tempo through the ingest gateway, so a browser event and its
 * API spans are correlated by time and user rather than a shared trace id —
 * propagating trace headers would need `sentry-trace` and `baggage` added to
 * the API Gateway CORS allow-list first.
 *
 * `glitchtipDsn` is empty until a project is created in the GlitchTip UI and
 * its DSN pasted into the environment file. With no DSN nothing is
 * initialised and no requests leave the browser.
 */
export function telemetryOptions(): Sentry.BrowserOptions {
    return {
        dsn: environment.glitchtipDsn,
        environment: environment.stage,
        integrations: [Sentry.browserTracingIntegration()],
        // Pageload and navigation timing — enough to see how the app performs
        // for users, without a firehose out of prod.
        tracesSampleRate: environment.production ? 0.1 : 1.0,
        // See the note above: no outgoing trace headers until CORS allows them.
        tracePropagationTargets: [],
        // Browser extensions and third-party scripts produce noise we can
        // neither reproduce nor act on. Deliberately NOT ignoring "Non-Error
        // promise rejection captured": an escaped HttpErrorResponse is not an
        // Error subclass, so that message covers our own failed API calls.
        ignoreErrors: ['ResizeObserver loop limit exceeded'],
    };
}

export function initTelemetry(): void {
    if (!environment.glitchtipDsn) {
        return;
    }

    Sentry.init(telemetryOptions());
}

/**
 * Reports uncaught Angular errors and instruments the router for navigation
 * timing. Returns nothing when no DSN is configured, leaving stock Angular
 * error handling in place.
 */
export function provideTelemetry(): (Provider | EnvironmentProviders)[] {
    if (!environment.glitchtipDsn) {
        return [];
    }

    return [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        // TraceService only subscribes to router events once something asks
        // for it, so instantiate it at startup.
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
    ];
}
